import { useI18n } from '@i18n';
import { Autocomplete, Checkbox, Chip, SxProps, TextField } from '@mui/material';
import { area_type_enum } from '@predium/enums';
import { ReactNode, Ref, useCallback, useMemo } from 'react';
import {
  TenantAreaListbox,
  TenantAreaListboxContext,
} from '@/sections/components/consumption-location/TenantAreaListbox';

export type SelectableArea = {
  id: number;
  area_type_id: area_type_enum;
  external_id: string;
  description: string;
};

type Props = {
  /** Areas of the selected building — only the tenant (MF) ones are offered. */
  areas: SelectableArea[];
  value: number[];
  onChange: (areaIds: number[]) => void;
  disabled?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: ReactNode;
  inputRef?: Ref<HTMLInputElement>;
  /** Rendered next to the select-all button inside the listbox. */
  listboxActions?: ReactNode;
  sx?: SxProps;
};

export function TenantAreaAutocomplete({
  areas,
  value,
  onChange,
  disabled = false,
  size,
  error = false,
  helperText,
  inputRef,
  listboxActions,
  sx,
}: Props) {
  const { t } = useI18n();

  const options = useMemo(
    () =>
      areas
        .filter(({ area_type_id }) => area_type_id === area_type_enum.MF)
        .map(({ id, external_id, description }) => ({
          label: [description, external_id].filter(Boolean).join(' ') || t('DataCollectionAreas_UnassignedRentalArea'),
          value: id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [areas, t],
  );

  const selectedOptions = useMemo(() => options.filter((option) => value.includes(option.value)), [options, value]);
  const allSelected = options.length > 0 && selectedOptions.length === options.length;

  const handleToggleSelectAll = useCallback(() => {
    onChange(allSelected ? [] : options.map((option) => option.value));
  }, [allSelected, onChange, options]);

  const listboxState = useMemo(
    () => ({ allSelected, onToggleSelectAll: handleToggleSelectAll, extraActions: listboxActions }),
    [allSelected, handleToggleSelectAll, listboxActions],
  );

  return (
    <TenantAreaListboxContext.Provider value={listboxState}>
      <Autocomplete
        size={size}
        disabled={disabled}
        multiple
        disableCloseOnSelect
        sx={sx}
        options={options}
        componentsProps={{ paper: { elevation: 2 } }}
        value={selectedOptions}
        onChange={(_, chosenOptions) => onChange(chosenOptions.map((option) => option.value))}
        ListboxComponent={TenantAreaListbox}
        renderTags={(chosenOptions) =>
          allSelected ? (
            <Chip label={t('DataCollectionAreas_TenantArea_AllAreas')} sx={{ mr: 1 }} onDelete={() => onChange([])} />
          ) : (
            <>
              {chosenOptions.map((option) => (
                <Chip
                  key={option.value}
                  label={option.label}
                  sx={{ mr: 1 }}
                  onDelete={() => onChange(value.filter((areaId) => areaId !== option.value))}
                />
              ))}
            </>
          )
        }
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value} style={{ paddingLeft: 8, paddingRight: 8 }}>
            <Checkbox size="medium" checked={selected} />
            <span style={{ flexGrow: 1, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {option.label}
            </span>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            error={error}
            helperText={helperText}
            label={t('DataCollection_ConsumptionAssociatedTenantAreaSelect-label')}
          />
        )}
      />
    </TenantAreaListboxContext.Provider>
  );
}
